import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import {
	IntegrationEventModel,
	OperationExecutionTypeModel,
	OperationExecutionTypesModel,
	OperationExposureModel,
	SchemaSummaryModel,
} from '../../../api/api';
import CreateEventHandlerButton from '../../EventHandlerDesigner/Components/CreateEventHandlerButton';
import CreateIntegrationEventButton from '../../IntegrationEventDesigner/Components/CreateIntegrationEventButton';
import CreateOperationDialog from '../../OperationDesigner/Components/CreateOperationDialog';

type ContextArtifactDialogsProps = {
	contextId: string;
	entityOpen: boolean;
	onEntityOpenChange: (open: boolean) => void;
	entityName: string;
	onEntityNameChange: (name: string) => void;
	entityDescription: string;
	onEntityDescriptionChange: (description: string) => void;
	onCreateEntity: () => void | Promise<void>;
	enumOpen: boolean;
	onEnumOpenChange: (open: boolean) => void;
	enumName: string;
	onEnumNameChange: (name: string) => void;
	enumDescription: string;
	onEnumDescriptionChange: (description: string) => void;
	onCreateEnum: () => void | Promise<void>;
	operationOpen: boolean;
	onOperationOpenChange: (open: boolean) => void;
	onCreateOperation: (
		name: string,
		description: string,
		executionType: OperationExecutionTypesModel,
		exposure: OperationExposureModel,
		selectedEntityIds: string[],
	) => Promise<string | void>;
	operationExecutionTypes: OperationExecutionTypeModel[];
	availableEntities: SchemaSummaryModel[];
	eventHandlerOpen: boolean;
	onEventHandlerOpenChange: (open: boolean) => void;
	availableEvents: IntegrationEventModel[];
	onCreateEventHandler: (integrationEventId: string, name?: string, description?: string) => Promise<void>;
	integrationEventOpen: boolean;
	onIntegrationEventOpenChange: (open: boolean) => void;
	onCreateIntegrationEvent: (name: string, description: string, contextId: string) => Promise<void>;
};

export default function ContextArtifactDialogs(props: ContextArtifactDialogsProps) {
	return (
		<>
			<CreateOperationDialog
				open={props.operationOpen}
				onOpenChange={props.onOperationOpenChange}
				onCreate={props.onCreateOperation}
				executionTypes={props.operationExecutionTypes}
				availableEntities={props.availableEntities}
			/>
			<CreateEventHandlerButton
				hideButton
				open={props.eventHandlerOpen}
				onOpenChange={props.onEventHandlerOpenChange}
				availableEvents={props.availableEvents}
				onCreateHandler={props.onCreateEventHandler}
			/>
			<CreateIntegrationEventButton
				hideButton
				open={props.integrationEventOpen}
				onOpenChange={props.onIntegrationEventOpenChange}
				contextId={props.contextId}
				onCreate={props.onCreateIntegrationEvent}
			/>

			<Dialog open={props.entityOpen} onClose={() => props.onEntityOpenChange(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Create New Entity</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							autoFocus
							label="Entity Name"
							value={props.entityName}
							onChange={(event) => props.onEntityNameChange(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && props.entityName.trim()) props.onCreateEntity();
							}}
							fullWidth
							required
						/>
						<TextField
							label="Description"
							value={props.entityDescription}
							onChange={(event) => props.onEntityDescriptionChange(event.target.value)}
							multiline
							rows={3}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => props.onEntityOpenChange(false)}>Cancel</Button>
					<Button onClick={props.onCreateEntity} variant="contained" disabled={!props.entityName.trim()}>
						Create
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={props.enumOpen} onClose={() => props.onEnumOpenChange(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Create New Enum</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							autoFocus
							label="Enum Name"
							value={props.enumName}
							onChange={(event) => props.onEnumNameChange(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' && props.enumName.trim()) props.onCreateEnum();
							}}
							fullWidth
							required
						/>
						<TextField
							label="Description"
							value={props.enumDescription}
							onChange={(event) => props.onEnumDescriptionChange(event.target.value)}
							multiline
							rows={3}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => props.onEnumOpenChange(false)}>Cancel</Button>
					<Button onClick={props.onCreateEnum} variant="contained" disabled={!props.enumName.trim()}>
						Create
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
